import { WebPlugin } from '@capacitor/core';
import { getApp } from 'firebase/app';
import { Bytes as FirebaseBytes, DocumentReference as FirebaseDocumentReference, GeoPoint as FirebaseGeoPoint, Timestamp as FirebaseTimestamp, addDoc, and, arrayRemove, arrayUnion, clearIndexedDbPersistence, collection, collectionGroup, connectFirestoreEmulator, deleteDoc, deleteField, disableNetwork, doc, enableNetwork, endAt, endBefore, getCountFromServer, getDoc, getDocs, getFirestore, increment, initializeFirestore, limit, limitToLast, memoryLocalCache, onSnapshot, or, orderBy, persistentLocalCache, persistentMultipleTabManager, persistentSingleTabManager, query, serverTimestamp, setDoc, startAfter, startAt, updateDoc, where, writeBatch, } from 'firebase/firestore';
import { Bytes } from './bytes';
import { DocumentReference } from './document-reference';
import { FieldValue } from './field-value';
import { GeoPoint } from './geopoint';
import { deserializeSpecialNumber, serializeSpecialNumber, } from './special-number';
import { Timestamp } from './timestamp';
function normalizeServerTimestamps(value) {
    return value === 'estimate' || value === 'previous' ? value : 'none';
}
export class FirebaseFirestoreWeb extends WebPlugin {
    constructor() {
        super(...arguments);
        this.unsubscribesMap = new Map();
        this.lastListenerId = 0;
    }
    async addCollectionGroupSnapshotListener(options, callback) {
        const collectionQuery = await this.buildCollectionQuery(options, 'collectionGroup');
        const unsubscribe = onSnapshot(collectionQuery, {
            includeMetadataChanges: options.includeMetadataChanges,
            source: options.source,
        }, snapshot => {
            const event = {
                snapshots: snapshot.docs.map(documentSnapshot => ({
                    id: documentSnapshot.id,
                    path: documentSnapshot.ref.path,
                    data: this.deserializeData(documentSnapshot.data({
                        serverTimestamps: normalizeServerTimestamps(options.serverTimestamps),
                    })),
                    metadata: {
                        hasPendingWrites: documentSnapshot.metadata.hasPendingWrites,
                        fromCache: documentSnapshot.metadata.fromCache,
                    },
                })),
            };
            callback(event, undefined);
        }, error => callback(null, error));
        const id = this.generateListenerId();
        this.unsubscribesMap.set(id, unsubscribe);
        return id;
    }
    async addCollectionSnapshotListener(options, callback) {
        const collectionQuery = await this.buildCollectionQuery(options, 'collection');
        const unsubscribe = onSnapshot(collectionQuery, {
            includeMetadataChanges: options.includeMetadataChanges,
            source: options.source,
        }, snapshot => {
            const event = {
                snapshots: snapshot.docs.map(documentSnapshot => ({
                    id: documentSnapshot.id,
                    path: documentSnapshot.ref.path,
                    data: this.deserializeData(documentSnapshot.data({
                        serverTimestamps: normalizeServerTimestamps(options.serverTimestamps),
                    })),
                    metadata: {
                        hasPendingWrites: documentSnapshot.metadata.hasPendingWrites,
                        fromCache: documentSnapshot.metadata.fromCache,
                    },
                })),
            };
            callback(event, undefined);
        }, error => callback(null, error));
        const id = this.generateListenerId();
        this.unsubscribesMap.set(id, unsubscribe);
        return id;
    }
    async addDocument(options) {
        const firestore = getFirestore();
        const { reference, data } = options;
        const documentReference = await addDoc(collection(firestore, reference), this.serializeData(data));
        return {
            reference: DocumentReference.fromPath(documentReference.path),
        };
    }
    async addDocumentSnapshotListener(options, callback) {
        const firestore = getFirestore();
        const unsubscribe = onSnapshot(doc(firestore, options.reference), {
            includeMetadataChanges: options.includeMetadataChanges,
            source: options.source,
        }, snapshot => {
            const data = snapshot.data({
                serverTimestamps: normalizeServerTimestamps(options.serverTimestamps),
            });
            const event = {
                snapshot: {
                    id: snapshot.id,
                    path: snapshot.ref.path,
                    data: (data === undefined
                        ? null
                        : this.deserializeData(data)),
                    metadata: {
                        hasPendingWrites: snapshot.metadata.hasPendingWrites,
                        fromCache: snapshot.metadata.fromCache,
                    },
                },
            };
            callback(event, undefined);
        }, error => callback(null, error));
        const id = this.generateListenerId();
        this.unsubscribesMap.set(id, unsubscribe);
        return id;
    }
    async clearPersistence() {
        const firestore = getFirestore();
        await clearIndexedDbPersistence(firestore);
    }
    async deleteDocument(options) {
        const firestore = getFirestore();
        const { reference } = options;
        await deleteDoc(doc(firestore, reference));
    }
    async disableNetwork() {
        const firestore = getFirestore();
        await disableNetwork(firestore);
    }
    async disablePersistence() {
        initializeFirestore(getApp(), {
            localCache: memoryLocalCache(),
        });
    }
    async enableNetwork() {
        const firestore = getFirestore();
        await enableNetwork(firestore);
    }
    async enablePersistence(options) {
        const tabManager = (options === null || options === void 0 ? void 0 : options.synchronizeTabs)
            ? persistentMultipleTabManager()
            : persistentSingleTabManager(undefined);
        initializeFirestore(getApp(), {
            localCache: persistentLocalCache({
                tabManager,
                cacheSizeBytes: options === null || options === void 0 ? void 0 : options.cacheSizeBytes,
            }),
        });
    }
    async getCollection(options) {
        const collectionQuery = await this.buildCollectionQuery(options, 'collection');
        const collectionSnapshot = await getDocs(collectionQuery);
        return {
            snapshots: collectionSnapshot.docs.map(documentSnapshot => ({
                id: documentSnapshot.id,
                path: documentSnapshot.ref.path,
                data: this.deserializeData(documentSnapshot.data()),
                metadata: {
                    hasPendingWrites: documentSnapshot.metadata.hasPendingWrites,
                    fromCache: documentSnapshot.metadata.fromCache,
                },
            })),
        };
    }
    async getCollectionGroup(options) {
        const collectionQuery = await this.buildCollectionQuery(options, 'collectionGroup');
        const collectionSnapshot = await getDocs(collectionQuery);
        return {
            snapshots: collectionSnapshot.docs.map(documentSnapshot => ({
                id: documentSnapshot.id,
                path: documentSnapshot.ref.path,
                data: this.deserializeData(documentSnapshot.data()),
                metadata: {
                    hasPendingWrites: documentSnapshot.metadata.hasPendingWrites,
                    fromCache: documentSnapshot.metadata.fromCache,
                },
            })),
        };
    }
    async getCountFromServer(options) {
        const collectionQuery = await this.buildCollectionQuery(options, 'collection');
        const snapshot = await getCountFromServer(collectionQuery);
        return { count: snapshot.data().count };
    }
    async getDocument(options) {
        const firestore = getFirestore();
        const { reference } = options;
        const documentSnapshot = await getDoc(doc(firestore, reference));
        const documentSnapshotData = documentSnapshot.data();
        return {
            snapshot: {
                id: documentSnapshot.id,
                path: documentSnapshot.ref.path,
                data: (documentSnapshotData === undefined
                    ? null
                    : this.deserializeData(documentSnapshotData)),
                metadata: {
                    hasPendingWrites: documentSnapshot.metadata.hasPendingWrites,
                    fromCache: documentSnapshot.metadata.fromCache,
                },
            },
        };
    }
    async removeAllListeners() {
        this.unsubscribesMap.forEach(unsubscribe => unsubscribe());
        this.unsubscribesMap.clear();
        await super.removeAllListeners();
    }
    async removeSnapshotListener(options) {
        const unsubscribe = this.unsubscribesMap.get(options.callbackId);
        if (!unsubscribe) {
            return;
        }
        unsubscribe();
        this.unsubscribesMap.delete(options.callbackId);
    }
    async setDocument(options) {
        const firestore = getFirestore();
        const { reference, data, merge } = options;
        await setDoc(doc(firestore, reference), this.serializeData(data), {
            merge,
        });
    }
    async updateDocument(options) {
        const firestore = getFirestore();
        const { reference, data } = options;
        await updateDoc(doc(firestore, reference), this.serializeData(data));
    }
    async useEmulator(options) {
        const firestore = getFirestore();
        const port = options.port || 8080;
        connectFirestoreEmulator(firestore, options.host, port);
    }
    async writeBatch(options) {
        const firestore = getFirestore();
        const { operations } = options;
        const batch = writeBatch(firestore);
        for (const operation of operations) {
            const { type, reference, data, options } = operation;
            const documentReference = doc(firestore, reference);
            switch (type) {
                case 'set':
                    batch.set(documentReference, this.serializeData(data !== null && data !== void 0 ? data : {}), options !== null && options !== void 0 ? options : {});
                    break;
                case 'update':
                    batch.update(documentReference, this.serializeData(data !== null && data !== void 0 ? data : {}));
                    break;
                case 'delete':
                    batch.delete(documentReference);
                    break;
            }
        }
        await batch.commit();
    }
    async buildCollectionQuery(options, type) {
        const firestore = getFirestore();
        let collectionQuery;
        if (options.compositeFilter) {
            const compositeFilter = this.buildFirebaseQueryCompositeFilterConstraint(options.compositeFilter);
            const queryConstraints = await this.buildFirebaseQueryNonFilterConstraints(options.queryConstraints || []);
            collectionQuery = query(type === 'collection'
                ? collection(firestore, options.reference)
                : collectionGroup(firestore, options.reference), compositeFilter, ...queryConstraints);
        }
        else {
            const queryConstraints = await this.buildFirebaseQueryConstraints(options.queryConstraints || []);
            collectionQuery = query(type === 'collection'
                ? collection(firestore, options.reference)
                : collectionGroup(firestore, options.reference), ...queryConstraints);
        }
        return collectionQuery;
    }
    buildFirebaseQueryCompositeFilterConstraint(compositeFilter) {
        const queryConstraints = this.buildFirebaseQueryFilterConstraints(compositeFilter.queryConstraints);
        if (compositeFilter.type === 'and') {
            return and(...queryConstraints);
        }
        else {
            return or(...queryConstraints);
        }
    }
    async buildFirebaseQueryConstraint(queryConstraint) {
        if (queryConstraint.type === 'where') {
            return this.buildFirebaseQueryFieldFilterConstraint(queryConstraint);
        }
        else {
            return await this.buildFirebaseQueryNonFilterConstraint(queryConstraint);
        }
    }
    async buildFirebaseQueryConstraints(queryConstraints) {
        const firebaseQueryConstraints = [];
        for (const queryConstraint of queryConstraints) {
            const firebaseQueryConstraint = await this.buildFirebaseQueryConstraint(queryConstraint);
            firebaseQueryConstraints.push(firebaseQueryConstraint);
        }
        return firebaseQueryConstraints;
    }
    buildFirebaseQueryFieldFilterConstraint(queryfilterConstraints) {
        return where(queryfilterConstraints.fieldPath, queryfilterConstraints.opStr, this.serializeFilterValue(queryfilterConstraints.value));
    }
    buildFirebaseQueryFilterConstraint(queryFilterConstraints) {
        if (queryFilterConstraints.type === 'where') {
            return this.buildFirebaseQueryFieldFilterConstraint(queryFilterConstraints);
        }
        else {
            return this.buildFirebaseQueryCompositeFilterConstraint(queryFilterConstraints);
        }
    }
    buildFirebaseQueryFilterConstraints(queryfilterConstraints) {
        const firebaseQueryFilterConstraints = [];
        for (const queryfilterConstraint of queryfilterConstraints) {
            const firebaseQueryFilterConstraint = this.buildFirebaseQueryFilterConstraint(queryfilterConstraint);
            firebaseQueryFilterConstraints.push(firebaseQueryFilterConstraint);
        }
        return firebaseQueryFilterConstraints;
    }
    async buildFirebaseQueryNonFilterConstraint(queryConstraints) {
        switch (queryConstraints.type) {
            case 'orderBy':
                return orderBy(queryConstraints.fieldPath, queryConstraints.directionStr);
            case 'limit':
                return limit(queryConstraints.limit);
            case 'limitToLast':
                return limitToLast(queryConstraints.limit);
            case 'startAt':
            case 'startAfter':
            case 'endAt':
            case 'endBefore': {
                const firestore = getFirestore();
                const documentSnapshot = await getDoc(doc(firestore, queryConstraints.reference));
                switch (queryConstraints.type) {
                    case 'startAt':
                        return startAt(documentSnapshot);
                    case 'startAfter':
                        return startAfter(documentSnapshot);
                    case 'endAt':
                        return endAt(documentSnapshot);
                    case 'endBefore':
                        return endBefore(documentSnapshot);
                }
            }
        }
    }
    async buildFirebaseQueryNonFilterConstraints(queryConstraints) {
        const firebaseQueryNonFilterConstraints = [];
        for (const queryConstraint of queryConstraints) {
            const firebaseQueryNonFilterConstraint = await this.buildFirebaseQueryNonFilterConstraint(queryConstraint);
            firebaseQueryNonFilterConstraints.push(firebaseQueryNonFilterConstraint);
        }
        return firebaseQueryNonFilterConstraints;
    }
    deserializeData(data) {
        if (data === null || data === undefined) {
            return data;
        }
        if (data instanceof FirebaseTimestamp) {
            return {
                __type__: 'timestamp',
                seconds: data.seconds,
                nanoseconds: data.nanoseconds,
            };
        }
        if (data instanceof FirebaseGeoPoint) {
            return {
                __type__: 'geopoint',
                latitude: data.latitude,
                longitude: data.longitude,
            };
        }
        if (data instanceof FirebaseDocumentReference) {
            return {
                __type__: 'documentReference',
                id: data.id,
                path: data.path,
            };
        }
        if (data instanceof FirebaseBytes) {
            return {
                __type__: 'bytes',
                bytes: data.toBase64(),
            };
        }
        if (typeof data === 'number' && !Number.isFinite(data)) {
            return serializeSpecialNumber(data);
        }
        if (Array.isArray(data)) {
            return data.map(item => this.deserializeData(item));
        }
        if (typeof data === 'object') {
            const result = {};
            for (const key of Object.keys(data)) {
                result[key] = this.deserializeData(data[key]);
            }
            return result;
        }
        return data;
    }
    serializeData(data) {
        if (data instanceof Timestamp) {
            return new FirebaseTimestamp(data.seconds, data.nanoseconds);
        }
        if (data instanceof GeoPoint) {
            return new FirebaseGeoPoint(data.latitude, data.longitude);
        }
        if (data instanceof Bytes) {
            return FirebaseBytes.fromBase64String(data.toBase64());
        }
        if (data instanceof FieldValue) {
            return this.serializeFieldValue(data.toJSON());
        }
        if (data === null || data === undefined) {
            return data;
        }
        if (Array.isArray(data)) {
            return data.map(item => this.serializeData(item));
        }
        if (typeof data === 'object') {
            if (data.__type__) {
                return this.serializeMarker(data);
            }
            const result = {};
            for (const key of Object.keys(data)) {
                result[key] = this.serializeData(data[key]);
            }
            return result;
        }
        return data;
    }
    serializeFieldValue(marker) {
        return this.serializeMarker(marker);
    }
    serializeFilterValue(value) {
        return this.serializeData(value);
    }
    serializeMarker(marker) {
        switch (marker.__type__) {
            case 'timestamp':
                return new FirebaseTimestamp(marker.seconds, marker.nanoseconds);
            case 'geopoint':
                return new FirebaseGeoPoint(marker.latitude, marker.longitude);
            case 'documentReference':
                return doc(getFirestore(), marker.path);
            case 'bytes':
                return FirebaseBytes.fromBase64String(marker.bytes);
            case 'number':
                return deserializeSpecialNumber(marker.value);
            case 'serverTimestamp':
                return serverTimestamp();
            case 'arrayUnion':
                return arrayUnion(...(marker.elements || []).map((e) => this.serializeData(e)));
            case 'arrayRemove':
                return arrayRemove(...(marker.elements || []).map((e) => this.serializeData(e)));
            case 'delete':
                return deleteField();
            case 'increment':
                return increment(marker.operand);
            default:
                return marker;
        }
    }
    generateListenerId() {
        return (++this.lastListenerId).toString();
    }
}
//# sourceMappingURL=web.js.map